import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { encryptData, decryptData, sanitizeData } from './encryption';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: async (key: string) => {
        const item = window.localStorage.getItem(key);
        if (!item) return null;
        return decryptData(item);
      },
      setItem: async (key: string, value: any) => {
        const encrypted = encryptData(value);
        window.localStorage.setItem(key, encrypted);
      },
      removeItem: async (key: string) => {
        window.localStorage.removeItem(key);
      },
    },
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-application-name': 'ClearPathRx',
      'Content-Security-Policy': "default-src 'self'; connect-src 'self' https://*.supabase.co https://*.hcaptcha.com; img-src 'self' data: https://*; script-src 'self' 'unsafe-inline' 'unsafe-eval' https://*.hcaptcha.com; style-src 'self' 'unsafe-inline'; frame-src https://*.hcaptcha.com; frame-ancestors 'self' https://*.hcaptcha.com;",
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

// Intercept and encrypt sensitive data before sending to Supabase
const encryptSensitiveData = (table: string, data: any) => {
  const sensitiveFields = ['notes', 'pharmacy_name', 'pharmacy_phone', 'prescription_number'];
  const encrypted = { ...data };

  for (const field of sensitiveFields) {
    if (field in data) {
      encrypted[field] = encryptData(data[field]);
    }
  }

  return encrypted;
};

// Decrypt sensitive data after receiving from Supabase
const decryptSensitiveData = (table: string, data: any) => {
  const sensitiveFields = ['notes', 'pharmacy_name', 'pharmacy_phone', 'prescription_number'];
  const decrypted = { ...data };

  for (const field of sensitiveFields) {
    if (field in data && data[field]) {
      decrypted[field] = decryptData(data[field]);
    }
  }

  return decrypted;
};

// Enhanced error handling with security context
export const handleSupabaseError = (error: any): string => {
  console.error('Supabase error:', {
    code: error.code,
    message: error.message,
    details: error.details,
    hint: error.hint,
    timestamp: new Date().toISOString()
  });

  // Sanitize error messages to prevent data leakage
  const sanitizedError = sanitizeData(error);

  if (sanitizedError.code === 'auth/invalid-email') {
    return 'Invalid email address';
  }
  if (sanitizedError.code === 'auth/wrong-password') {
    return 'Incorrect password';
  }
  if (sanitizedError.code === 'auth/user-not-found') {
    return 'No account found with this email';
  }
  if (sanitizedError.code === '23505') {
    return 'This email is already registered';
  }
  if (sanitizedError.code === '42501') {
    return 'You do not have permission to perform this action';
  }
  if (sanitizedError.message?.includes('captcha')) {
    return 'Please complete the captcha verification';
  }

  return 'An unexpected error occurred. Please try again later.';
};

// Enhanced Supabase client with encryption
export const secureSupabase = {
  async insert(table: string, data: any) {
    const sanitizedData = sanitizeData(data);
    const encryptedData = encryptSensitiveData(table, sanitizedData);
    const { data: result, error } = await supabase.from(table).insert(encryptedData).select();
    if (error) throw new Error(handleSupabaseError(error));
    return result ? result.map(row => decryptSensitiveData(table, row)) : null;
  },

  async update(table: string, id: string, data: any) {
    const sanitizedData = sanitizeData(data);
    const encryptedData = encryptSensitiveData(table, sanitizedData);
    const { data: result, error } = await supabase.from(table).update(encryptedData).eq('id', id).select();
    if (error) throw new Error(handleSupabaseError(error));
    return result ? result.map(row => decryptSensitiveData(table, row)) : null;
  },

  async select(table: string, query: any = {}) {
    const { data: result, error } = await supabase.from(table).select(query);
    if (error) throw new Error(handleSupabaseError(error));
    return result ? result.map(row => decryptSensitiveData(table, row)) : null;
  }
};